import * as React from 'react';
import { ListLayoutProps } from '..';
import { useCards } from 'hooks/useCards';
import { useListPagination } from 'hooks/useListPagination';
import { useTranslation } from 'hooks/useTranslation';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import { PlaceholderCard } from 'components/PlaceholderCard';
import { Filters } from 'components/List/Filters';
import { useFilters } from 'components/List/Filters/useFilters';
import { ListNotifications } from 'components/List/ListNotifications/ListNotifications';
import CardPage from './CardPage';
import ScrollButton from './ScrollButton';
import styles from './VerticalList.module.scss';
import { useHideEmptyList } from '../hooks/useHideEmptyList';
import { ListMetadata } from '../ListMetadata';
import { useListEngagementData } from 'components/SnowplowEngagementAnalytics/hooks';

const PAGE_SIZE = 10;

function isActiveCardOnPage(
  pageIndex: number,
  activeCardIndex: number
): boolean {
  return pageIndex === Math.floor(activeCardIndex / PAGE_SIZE);
}

function getActiveCardIndexOfPage(
  pageIndex: number,
  activeCardIndex: number | undefined
): number | undefined {
  if (
    activeCardIndex !== undefined &&
    isActiveCardOnPage(pageIndex, activeCardIndex)
  ) {
    return activeCardIndex % PAGE_SIZE;
  }
}

export const VerticalList: React.FunctionComponent<ListLayoutProps> = ({
  hideList,
  list,
  listKey,
  index,
  notificationComponent,
  tabTitle,
}) => {
  const { cursors = {}, filters = [] } = list;
  const listContentId = sanitizeHtmlId(listKey + 'content');

  const {
    visiblePages,
    showMore,
    reset: resetListPaging,
  } = useListPagination(listKey);
  const t = useTranslation();

  const {
    selectedFilterOptions,
    selectFilterOption,
    filterParameters,
    filterDefaultsSelected,
    filterStateKey,
  } = useFilters({
    listIndex: index,
    filters,
    resetListPaging,
  });
  const {
    cards: initialCards,
    errored: hasInitialCardFetchErrored,
    loading: isLoadingFirstCards,
    meta: initialCardsMeta,
    notifications,
    totalCount: totalCardCount,
  } = useCards({
    source: list.source,
    pageIndex: visiblePages - 1,
    pageSize: PAGE_SIZE,
    filterParameters,
  });

  useHideEmptyList({
    cardCount: totalCardCount,
    hasCardFetchErrored: hasInitialCardFetchErrored,
    hideList,
    isListLoadingFirstCards: isLoadingFirstCards,
    notifications,
  });

  const visibleCardCount = visiblePages * PAGE_SIZE;
  const areAllCardsVisible = visibleCardCount >= totalCardCount;
  const isShowMoreVisible = !areAllCardsVisible;

  const onShowMore = () => {
    if (!isLoadingFirstCards && !areAllCardsVisible) {
      showMore();
    }
  };

  const activeCardIndex = filterDefaultsSelected
    ? cursors.playlist?.offset
    : undefined;
  const groupEngagementData = useListEngagementData({
    cardPageMeta: initialCardsMeta,
    isPaginated: true,
    list,
    listIndex: index,
    tabTitle,
  });

  return (
    <div className="VerticalList">
      <ListMetadata cards={initialCards} />
      <Container>
        <ListNotifications
          notifications={notifications}
          listStyle={list.style}
          notificationComponent={notificationComponent}
        />
        <GridContainer>
          {filters.length > 0 ? (
            <GridElement mobile={12} tablet={3} desktopSmall={5}>
              <Filters
                filters={filters}
                listContentId={listContentId}
                selectFilterOption={selectFilterOption}
                selectedFilterOptions={selectedFilterOptions}
              />
            </GridElement>
          ) : null}
          <GridElement mobile={12} tablet={9} desktopSmall={17} desktopXL={14}>
            <div className={styles.wrapper}>
              <ul className={styles.list} id={listContentId}>
                {Array.from({ length: visiblePages }, (_, pageIndex) => (
                  <CardPage
                    key={`${filterStateKey}-${pageIndex}`}
                    pageKey={`${listKey}-${filterStateKey}-${pageIndex}`}
                    listStyle={list.style}
                    pageIndex={pageIndex}
                    pageSize={PAGE_SIZE}
                    source={list.source}
                    filterParameters={filterParameters}
                    activeCardIndex={getActiveCardIndexOfPage(
                      pageIndex,
                      activeCardIndex
                    )}
                    groupEngagementData={groupEngagementData}
                    totalCount={totalCardCount}
                  />
                ))}
                {!areAllCardsVisible ? (
                  <CardPage
                    pageKey={`${listKey}-${filterStateKey}-${visiblePages}`}
                    listStyle={list.style}
                    pageIndex={visiblePages}
                    pageSize={PAGE_SIZE}
                    source={list.source}
                    hidden
                    filterParameters={filterParameters}
                    groupEngagementData={groupEngagementData}
                    totalCount={totalCardCount}
                  />
                ) : null}
                {isLoadingFirstCards &&
                  Array.from({ length: PAGE_SIZE }, (_, i) => (
                    <li className={styles.listItem} key={i}>
                      <PlaceholderCard listStyle={list.style} />
                    </li>
                  ))}
              </ul>
              {isShowMoreVisible ? (
                <ScrollButton onClick={onShowMore} text={t('showMore')} />
              ) : null}
            </div>
          </GridElement>
        </GridContainer>
      </Container>
    </div>
  );
};
